import React from "react";
import { useParams, Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ShieldCheck, Building2, Landmark, Lightbulb, CheckCircle2, MessageCircle, ArrowRight, FileText } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { popularCountries, customsProcess, customsNotes, contact } from "../mock";

export default function CustomsClearanceDetail() {
  const { slug } = useParams();
  const country = popularCountries.find((c) => c.slug === slug);

  if (!country) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-slate-900 mb-4">Country not found</h1>
        <Link to="/customs-clearance" className="text-teal-600 font-semibold">Back to Customs Clearance</Link>
      </div>
    );
  }

  const note = customsNotes[slug] || {
    authority: `${country.name} Customs Authority`,
    duty: "Personal-use medicine shipments generally clear with complete documentation; any applicable duty is paid directly by the receiver.",
    tip: "Keep a valid prescription and purchase bill ready, and declare ingredients clearly for herbal or homeopathic products.",
  };

  const documents = [
    "Valid prescription (soft copy) from a registered practitioner",
    "Pharmacy purchase bill / retail invoice",
    "Ingredient or composition list (for Ayurvedic & Homeopathy)",
    "Commercial export invoice (prepared by ShipMeds)",
  ];

  const others = popularCountries.filter((c) => c.slug !== slug).slice(0, 6);

  return (
    <>
      <Seo
        title={`Customs Clearance in ${country.name} for Medicine Shipments | ShipMeds`}
        description={`How ShipMeds clears Ayurvedic & Homeopathy medicines through ${country.name} customs (${note.authority}). Documents, duty info and our 100% clearance guarantee.`}
        path={`/customs-clearance/${country.slug}`}
      />
      <PageHero title={`Customs Clearance in ${country.name}`} subtitle={`How ShipMeds clears your medicines through ${country.name} customs - with a 100% clearance guarantee.`} crumb={country.name} />

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-4 mb-6">
              <img src={`https://flagcdn.com/w160/${country.code}.png`} alt={country.name} className="w-16 h-11 object-cover rounded-md shadow-sm" />
              <div>
                <h2 className="text-2xl font-extrabold text-slate-900">Shipping Medicines to {country.name}</h2>
                <p className="text-slate-500 text-sm">Typical clearance & delivery: {country.days} working days</p>
              </div>
            </div>
            <p className="text-slate-600 leading-relaxed mb-8">When your parcel arrives in {country.name}, ShipMeds manages the entire customs clearance process on your behalf. Our documentation is prepared to satisfy local requirements, so both branded and unbranded Ayurvedic and Homeopathy medicines move through customs smoothly.</p>

            {/* Authority + duty */}
            <div className="grid sm:grid-cols-2 gap-5 mb-8">
              <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
                <div className="w-11 h-11 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center mb-3"><Landmark className="w-5 h-5" /></div>
                <h4 className="font-bold text-slate-900 mb-1">Customs Authority</h4>
                <p className="text-sm text-slate-500 leading-relaxed">{note.authority}</p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
                <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-3"><Building2 className="w-5 h-5" /></div>
                <h4 className="font-bold text-slate-900 mb-1">Duty & Charges</h4>
                <p className="text-sm text-slate-500 leading-relaxed">{note.duty}</p>
              </div>
            </div>

            {/* Process */}
            <h3 className="text-xl font-bold text-slate-900 mb-4">Our Clearance Process for {country.name}</h3>
            <div className="space-y-4 mb-8">
              {customsProcess.map((s) => {
                const Icon = Icons[s.icon] || FileText;
                return (
                  <div key={s.no} className="flex gap-4 bg-white rounded-2xl p-5 border border-slate-100 card-shadow">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-teal-500 to-blue-600 text-white flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1">{s.no}. {s.title}</h4>
                      <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Documents */}
            <h3 className="text-xl font-bold text-slate-900 mb-4">Documents We Prepare</h3>
            <ul className="grid sm:grid-cols-2 gap-3 mb-8">
              {documents.map((d) => (
                <li key={d} className="flex items-start gap-2 bg-slate-50 rounded-xl p-4 border border-slate-100 text-sm text-slate-700"><CheckCircle2 className="w-5 h-5 text-teal-500 shrink-0 mt-0.5" />{d}</li>
              ))}
            </ul>

            {/* Tip */}
            <div className="flex gap-4 bg-amber-50 border border-amber-100 rounded-2xl p-5">
              <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0"><Lightbulb className="w-5 h-5" /></div>
              <div>
                <h4 className="font-bold text-slate-900 mb-1">Pro Tip for {country.name}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{note.tip}</p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:sticky lg:top-28 h-fit space-y-6">
            <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-6 text-white">
              <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center mb-3"><ShieldCheck className="w-7 h-7" /></div>
              <h3 className="font-bold text-lg mb-2">100% Clearance Guarantee</h3>
              <p className="text-teal-100 text-sm leading-relaxed mb-5">We guarantee destination customs clearance on Ayurvedic & Homeopathy shipments to {country.name}.</p>
              <a href={contact.whatsapp} target="_blank" rel="noreferrer" className="w-full inline-flex items-center justify-center gap-2 bg-white text-teal-700 font-bold px-5 py-3 rounded-lg hover:bg-teal-50 transition-colors mb-3"><MessageCircle className="w-5 h-5" /> Book on WhatsApp</a>
              <Link to={`/country/${country.slug}`} className="w-full inline-flex items-center justify-center gap-2 bg-teal-800/40 text-white font-semibold px-5 py-3 rounded-lg hover:bg-teal-800/60 transition-colors">View {country.name} Charges <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="bg-white border border-slate-100 card-shadow rounded-2xl p-6">
              <h3 className="font-bold text-slate-900 mb-4">Clearance in Other Countries</h3>
              <ul className="space-y-2">
                {others.map((c) => (
                  <li key={c.slug}>
                    <Link to={`/customs-clearance/${c.slug}`} className="group flex items-center justify-between text-sm text-slate-600 hover:text-teal-600 transition-colors py-1">
                      <span className="flex items-center gap-2"><img src={`https://flagcdn.com/w40/${c.code}.png`} alt={c.name} className="w-5 h-3.5 object-cover rounded-sm" /> {c.name}</span>
                      <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                    </Link>
                  </li>
                ))}
              </ul>
              <Link to="/customs-clearance" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-600 hover:text-teal-700">All countries <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
